import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api.js";
import {
  describeSubscription,
  useBillingState,
  useOpenBillingPortal,
} from "../lib/use-billing-state.js";

/**
 * `/billing` route — the org's billing dashboard.
 *
 * Shows three sections:
 *   1. Subscription summary (status + tier + renewal date)
 *   2. Add-ons (extra seats, extra teams)
 *   3. Hardware orders
 *
 * Actions:
 *   • "Plan starten"  — first-time subscribe → Stripe Checkout
 *   • "Verwalten"     — open Customer Portal (cancel, update card, invoices)
 *
 * Hardware ordering and add-on quantity editing happen on dedicated pages
 * later in Phase 5; this is the central hub.
 */
export function BillingPage() {
  const billing = useBillingState();
  const portal = useOpenBillingPortal();
  const [interval, setInterval] = useState<"month" | "year">("year");
  const [extraSeats, setExtraSeats] = useState(0);
  const [extraTeams, setExtraTeams] = useState(0);
  const [extraStorageBlocks, setExtraStorageBlocks] = useState(0);

  const catalog = useQuery({
    queryKey: ["billing", "catalog"],
    queryFn: () => api().billing.catalog(),
    staleTime: 30 * 60 * 1000,
  });

  const startCheckout = useMutation({
    mutationFn: async () => {
      const landingUrl =
        (import.meta as any).env?.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781";
      const { url } = await api().billing.startSubscriptionCheckout({
        interval,
        extraSeats,
        extraTeams,
        extraStorageBlocks,
        returnTo: `${landingUrl}/dashboard/billing?checkout=success`,
      });
      if (!url) throw new Error("Checkout-Session ohne URL");
      window.location.href = url;
    },
  });

  if (billing.isPending) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-300">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (billing.isError) {
    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-100">
        Konnte Billing-Status nicht laden: {(billing.error as Error).message}
      </div>
    );
  }

  const state = billing.data;
  const desc = describeSubscription(state);
  const hasSubscription = Boolean(state?.subscription);
  // A valid manual license overrides Stripe: no "kein Abo" text and no
  // checkout offer while it runs.
  const manual = state?.effective?.kind === "manual" ? state.effective : null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-6 text-slate-100">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Abrechnung</h1>
        <p className="text-sm text-slate-300">
          Subscription, Erweiterungen und Hardware-Bestellungen deines Clubs.
        </p>
      </header>

      {/* ── Subscription summary ───────────────────────────────────────── */}
      <section className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-medium">Subscription</h2>
          <span
            className={
              desc.tone === "ok"
                ? "rounded-full bg-emerald-500/20 px-3 py-0.5 text-xs font-medium text-emerald-200"
                : desc.tone === "trial"
                  ? "rounded-full bg-sky-500/20 px-3 py-0.5 text-xs font-medium text-sky-200"
                  : desc.tone === "warn"
                    ? "rounded-full bg-amber-500/20 px-3 py-0.5 text-xs font-medium text-amber-100"
                    : desc.tone === "danger"
                      ? "rounded-full bg-red-500/20 px-3 py-0.5 text-xs font-medium text-red-200"
                      : "rounded-full bg-slate-500/20 px-3 py-0.5 text-xs font-medium text-slate-200"
            }
          >
            {desc.label}
          </span>
        </div>
        {hasSubscription ? (
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-400">Intervall</dt>
              <dd>{state?.subscription?.baseInterval === "year" ? "Jährlich" : "Monatlich"}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Nächste Verlängerung</dt>
              <dd>
                {state?.subscription?.currentPeriodEnd
                  ? new Date(state.subscription.currentPeriodEnd).toLocaleDateString("de-DE")
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-400">Extra Seats</dt>
              <dd>{state?.subscription?.seatsExtra ?? 0}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Extra Teams</dt>
              <dd>{state?.subscription?.teamsExtra ?? 0}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Cloud-Speicher</dt>
              <dd>
                {(state?.subscription?.storageExtraBlocks ?? 0) > 0
                  ? `${1 + state!.subscription!.storageExtraBlocks * 5} GB`
                  : "1 GB (inklusive)"}
              </dd>
            </div>
          </dl>
        ) : manual ? (
          <p className="text-sm text-slate-300">
            Deine Lizenz wird manuell verwaltet
            {manual.expiresAt
              ? ` und ist gültig bis ${new Date(manual.expiresAt).toLocaleDateString("de-DE")}.`
              : " und ist unbefristet gültig."}
          </p>
        ) : (
          <p className="text-sm text-slate-300">
            Du hast aktuell keine aktive Stripe-Subscription. Starte eine neue, um die Vollversion
            freizuschalten.
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {hasSubscription ? (
            <button
              type="button"
              disabled={portal.isPending}
              onClick={() => portal.mutate(`${window.location.origin}/billing`)}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium hover:bg-white/20 disabled:opacity-60"
            >
              {portal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Im Stripe-Portal verwalten
            </button>
          ) : null}
        </div>
      </section>

      {/* ── Start subscription (only if none) ─────────────────────────── */}
      {!hasSubscription && !manual && (
        <section className="space-y-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
          <h2 className="text-lg font-medium">Plan starten</h2>
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-3">
              <span className="w-32 text-slate-300">Intervall</span>
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value as "month" | "year")}
                className="rounded-lg border border-white/20 bg-slate-900/60 px-3 py-1.5"
              >
                <option value="month">Monatlich</option>
                <option value="year">Jährlich (günstiger)</option>
              </select>
            </label>
            <label className="flex items-center gap-3">
              <span className="w-32 text-slate-300">Extra Seats</span>
              <input
                type="number"
                min={0}
                value={extraSeats}
                onChange={(e) => setExtraSeats(Math.max(0, Number(e.target.value) || 0))}
                className="w-24 rounded-lg border border-white/20 bg-slate-900/60 px-3 py-1.5"
              />
            </label>
            <label className="flex items-center gap-3">
              <span className="w-32 text-slate-300">Extra Teams</span>
              <input
                type="number"
                min={0}
                value={extraTeams}
                onChange={(e) => setExtraTeams(Math.max(0, Number(e.target.value) || 0))}
                className="w-24 rounded-lg border border-white/20 bg-slate-900/60 px-3 py-1.5"
              />
            </label>
            <label className="flex items-center gap-3">
              <span className="w-32 text-slate-300">5 GB-Blöcke</span>
              <input
                type="number"
                min={0}
                max={200}
                value={extraStorageBlocks}
                onChange={(e) => setExtraStorageBlocks(Math.max(0, Number(e.target.value) || 0))}
                className="w-24 rounded-lg border border-white/20 bg-slate-900/60 px-3 py-1.5"
              />
              <span className="text-xs text-slate-400">à €2/Monat</span>
            </label>
          </div>
          {startCheckout.isError && (
            <p className="text-sm text-red-300">{(startCheckout.error as Error).message}</p>
          )}
          {catalog.data?.missing.length ? (
            <p className="text-xs text-amber-200">
              Hinweis: Diese Preise fehlen im Stripe-Katalog — {catalog.data.missing.join(", ")}.
              Bitte `pnpm --filter @coach/server stripe:setup-catalog` ausführen.
            </p>
          ) : null}
          <button
            type="button"
            disabled={startCheckout.isPending}
            onClick={() => startCheckout.mutate()}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60"
          >
            {startCheckout.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Mit 7-Tage-Test starten
          </button>
        </section>
      )}

      {/* ── Hardware orders ───────────────────────────────────────────── */}
      <section className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-lg font-medium">Hardware</h2>
        {state?.hardware && state.hardware.length > 0 ? (
          <ul className="divide-y divide-white/10 text-sm">
            {state.hardware.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 py-2">
                <span>
                  <strong>{h.sku}</strong>{" "}
                  {h.quantity > 1 ? <span className="text-slate-300">×{h.quantity}</span> : null}{" "}
                  <span className="text-slate-400">
                    ({h.mode === "lease" ? "Leasing" : "Kauf"})
                  </span>
                </span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs">{h.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">
            Noch keine Hardware-Bestellungen. Tablets können beim ersten Checkout oder über das
            Stripe-Portal hinzugefügt werden.
          </p>
        )}
      </section>

      {/* ── Cloud storage usage ────────────────────────────────────── */}
      {state?.storage ? (
        <section className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium">Cloud-Speicher</h2>
            <span className="text-xs text-slate-400">
              {formatBytes(state.storage.usedBytes)} / {formatBytes(state.storage.limitBytes)}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className={
                state.storage.usedBytes / Math.max(1, state.storage.limitBytes) > 0.9
                  ? "h-full bg-red-500"
                  : "h-full bg-emerald-500"
              }
              style={{
                width: `${Math.min(100, Math.round((state.storage.usedBytes / Math.max(1, state.storage.limitBytes)) * 100))}%`,
              }}
            />
          </div>
          <p className="text-xs text-slate-400">
            1&nbsp;GB sind inklusive. Aktuell {state.storage.extraBlocks}&nbsp;× 5&nbsp;GB-Block(s)
            gebucht. Anpassen über das Stripe-Portal.
          </p>
        </section>
      ) : null}

      {/* ── Entitlements (read-only) ──────────────────────────────────── */}
      {state?.entitlements?.length ? (
        <section className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-5">
          <h2 className="text-lg font-medium">Freigeschaltete Features</h2>
          <ul className="flex flex-wrap gap-2 text-xs">
            {state.entitlements.map((e) => (
              <li key={e} className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-200">
                {e.replace(/^feature\./, "")}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}
